import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3 } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";

export default function ClassAnalytics() {
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [examId, setExamId] = useState("");
  const [cls, setCls] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/exams").then((r) => { setExams(r.data); if (r.data[0]) setExamId(r.data[0].id); });
    api.get("/classes").then((r) => { setClasses(r.data); if (r.data[0]) setCls(r.data[0].name); });
  }, []);

  useEffect(() => {
    if (!examId || !cls) return;
    const [grade, branch] = cls.split("-");
    api.get(`/exams/${examId}/class-analytics`, { params: { grade, branch } }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [examId, cls]);

  const pie = useMemo(() => data ? [
    { name: "Doğru", value: data.totals?.correct || 0, color: "#10b981" },
    { name: "Yanlış", value: data.totals?.wrong || 0, color: "#ef4444" },
    { name: "Boş", value: data.totals?.blank || 0, color: "#94a3b8" },
  ] : [], [data]);

  return (
    <div>
      <PageHeader title="Sınıf Analizi" desc="Şube bazlı KTT performansı" />
      <Card className="p-5 bg-card border-border mb-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Sınav</Label>
            <Select value={examId} onValueChange={setExamId}><SelectTrigger data-testid="ca-exam"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">{exams.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-1.5"><Label>Sınıf</Label>
            <Select value={cls} onValueChange={setCls}><SelectTrigger data-testid="ca-class"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">{classes.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
      </Card>

      {!data || data.count === 0 ? (
        <EmptyState icon={BarChart3} title="Veri yok" desc="Seçilen sınıf için bu sınavda henüz sonuç bulunmuyor." />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className="p-4 bg-card border-border"><div className="text-xs uppercase text-muted-foreground font-semibold">Öğrenci</div><div className="text-2xl font-bold mt-1">{data.count}</div></Card>
            <Card className="p-4 bg-card border-border"><div className="text-xs uppercase text-muted-foreground font-semibold">Sınıf Ort. Net</div><div className="text-2xl font-bold mt-1">{data.class_average_net}</div></Card>
            <Card className="p-4 bg-card border-border"><div className="text-xs uppercase text-muted-foreground font-semibold">Toplam Doğru</div><div className="text-2xl font-bold mt-1 text-emerald-600">{data.totals.correct}</div></Card>
            <Card className="p-4 bg-card border-border"><div className="text-xs uppercase text-muted-foreground font-semibold">Toplam Yanlış</div><div className="text-2xl font-bold mt-1 text-red-600">{data.totals.wrong}</div></Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="p-5 bg-card border-border">
              <h3 className="font-semibold mb-4">Ders Başarı Ortalaması (Net)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.subjects}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} interval={0} angle={-20} textAnchor="end" height={70} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Bar dataKey="avg_net" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} name="Ort. Net" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-5 bg-card border-border">
              <h3 className="font-semibold mb-4">Doğru / Yanlış / Boş Dağılımı</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={pie} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} label>
                    {pie.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Legend />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            </Card>
            <Card className="lg:col-span-2 p-5 bg-card border-border">
              <h3 className="font-semibold mb-4">Öğrenci Net Dağılımı</h3>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={data.distribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} interval={0} angle={-35} textAnchor="end" height={90} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Bar dataKey="net" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} name="Net" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
